import React from 'react';
import { useParams } from 'react-router-dom';
import { TableData } from '@/components';
import { Column } from '@/types/common';
import {
  GoodsReceiptSummary,
  PurchaseOrder,
  PurchaseOrderLine,
  warehouseApi,
} from '@/services/api/warehouse';
import type { PoListMode } from './PurchaseOrderListPage';
import {
  codeCls,
  DetailHead,
  DetailSkeleton,
  EmptyState,
  fmtDateTime,
  fmtQty,
  hubName,
  KV,
  LoadError,
  metaCls,
  Notice,
  numCls,
  PO_STATUS,
  PO_TYPE_LABEL,
  poSourceOf,
  RECEIPT_STATUS,
  Section,
  StatusBadge,
  Tag,
  Timeline,
  TimelineEntry,
  useLoad,
  WAREHOUSE_PATHS,
  productNameCls,
} from './shared';

const LINE_COLUMNS: Column<PurchaseOrderLine>[] = [
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => (
      <span className={`${codeCls} block max-w-[150px] truncate`} title={l.sku}>
        {l.sku}
      </span>
    ),
  },
  {
    key: 'itemName',
    title: 'Product name',
    render: (_v, l) => (
      <span className={productNameCls} title={l.itemName}>
        {l.itemName}
      </span>
    ),
  },
  {
    key: 'uomName',
    title: 'UoM',
    render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
  },
  {
    key: 'qty',
    title: 'Qty',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qty)}</span>,
  },
  {
    key: 'qtyReceived',
    title: 'Received',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qtyReceived)}</span>,
  },
  {
    key: 'qtyRemaining',
    title: 'Remaining',
    align: 'right',
    render: (_v, l) => (
      <span
        className={`text-sm font-semibold tabular-nums ${
          l.qtyRemaining > 0 ? 'text-text-strong-950' : 'text-text-soft-400'
        }`}
      >
        {fmtQty(Math.max(0, l.qtyRemaining))}
      </span>
    ),
  },
];

const RECEIPT_COLUMNS: Column<GoodsReceiptSummary>[] = [
  {
    key: 'code',
    title: 'GR number',
    render: (_v, r) => <span className={codeCls}>{r.code}</span>,
  },
  {
    key: 'arrivedAt',
    title: 'Tiba',
    render: (_v, r) => (
      <span className="text-xs text-text-sub-600">
        {fmtDateTime(r.arrivedAt ?? r.createdAt)}
      </span>
    ),
  },
  {
    key: 'deliveryNoteNo',
    title: 'Surat jalan',
    render: (_v, r) => (
      <span className={r.deliveryNoteNo ? codeCls : metaCls}>
        {r.deliveryNoteNo || '—'}
      </span>
    ),
  },
  {
    key: 'qty',
    title: 'Qty',
    align: 'right',
    render: (_v, r) => (
      <div className="text-right">
        <p className={numCls}>{fmtQty(r.totalQty)}</p>
        <p className={metaCls}>{r.lineCount} baris</p>
      </div>
    ),
  },
  {
    key: 'status',
    title: 'Status',
    render: (_v, r) => (
      <StatusBadge
        map={{
          DRAFT: { label: 'Draft', tone: 'neutral' },
          POSTED: { label: 'Posted', tone: 'success' },
        }}
        value={r.status}
      />
    ),
  },
];

/** draft → confirmed → receipts → finished / cancelled, newest first. */
const historyOf = (
  order: PurchaseOrder,
  receipts: GoodsReceiptSummary[]
): TimelineEntry[] => {
  const entries: TimelineEntry[] = [
    {
      id: 'created',
      at: order.createdAt,
      title: 'PO dibuat',
      badge: <StatusBadge map={PO_STATUS} value="DRAFT" />,
    },
  ];
  if (order.confirmedAt) {
    entries.push({
      id: 'confirmed',
      at: order.confirmedAt,
      title: 'PO di-confirm · inbound request terbit',
      badge: <StatusBadge map={PO_STATUS} value="CONFIRMED" />,
    });
  }
  const posted = receipts
    .filter((r) => r.postedAt)
    .sort((a, b) => ((a.postedAt ?? '') < (b.postedAt ?? '') ? -1 : 1));
  posted.forEach((r) =>
    entries.push({
      id: `gr-${r.id}`,
      at: r.postedAt as string,
      title: `Goods receipt ${r.code}`,
      note: [
        `${fmtQty(r.totalQty)} diterima · ${r.lineCount} baris`,
        r.deliveryNoteNo ? `surat jalan ${r.deliveryNoteNo}` : null,
      ]
        .filter(Boolean)
        .join(' · '),
      badge: <StatusBadge map={RECEIPT_STATUS} value="PARTIAL_RECEIPT" />,
    })
  );
  const last = posted[posted.length - 1];
  if (order.receiptStatus === 'FINISHED' && last?.postedAt) {
    entries.push({
      id: 'finished',
      at: last.postedAt,
      title: 'Penerimaan selesai',
      note:
        order.totalReceived < order.totalQty
          ? 'Sisa PO ditutup'
          : 'Semua barang diterima',
      badge: <StatusBadge map={RECEIPT_STATUS} value="FINISHED" />,
    });
  }
  if (order.cancelledAt) {
    entries.push({
      id: 'cancelled',
      at: order.cancelledAt,
      title: 'PO dibatalkan',
      note: order.cancellationReason ?? undefined,
      badge: <StatusBadge map={PO_STATUS} value="CANCELLED" />,
    });
  }
  // Newest first; at the same instant the later step comes first.
  return entries
    .map((e, i) => ({ e, i }))
    .sort((a, b) => (a.e.at === b.e.at ? b.i - a.i : a.e.at < b.e.at ? 1 : -1))
    .map(({ e }) => e);
};

const PurchaseOrderDetailPage: React.FC<{ mode: PoListMode }> = ({ mode }) => {
  const { id = '' } = useParams();
  const inbound = mode === 'inbound';
  const backTo = inbound
    ? WAREHOUSE_PATHS.inbound
    : WAREHOUSE_PATHS.purchaseOrder;
  const backLabel = inbound ? 'Inbound request' : 'Purchase order';
  const detail = useLoad(
    () =>
      inbound
        ? warehouseApi.getInboundRequest(id)
        : warehouseApi.getPurchaseOrder(id),
    [inbound, id]
  );

  if (detail.loading && !detail.data) return <DetailSkeleton />;
  if (detail.error || !detail.data) {
    return (
      <div className="space-y-5 p-4 sm:p-6">
        <DetailHead backTo={backTo} backLabel={backLabel} title={id} />
        <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0">
          <LoadError
            message={detail.error ?? `${backLabel} tidak ditemukan`}
            onRetry={detail.reload}
          />
        </div>
      </div>
    );
  }

  const { order: x, lines, goodsReceipts } = detail.data;
  const src = poSourceOf(x);

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <DetailHead
        backTo={backTo}
        backLabel={backLabel}
        title={x.code}
        badges={
          <>
            {inbound ? (
              <StatusBadge map={RECEIPT_STATUS} value={x.receiptStatus} />
            ) : (
              <StatusBadge map={PO_STATUS} value={x.status} />
            )}
            <Tag>{PO_TYPE_LABEL[x.type ?? 'REGULAR']}</Tag>
          </>
        }
        meta={
          <>
            Dibuat {fmtDateTime(x.createdAt)}
            {x.confirmedAt && ` · confirmed ${fmtDateTime(x.confirmedAt)}`}
          </>
        }
      />

      {x.status === 'CANCELLED' && (
        <Notice title="PO dibatalkan">
          {x.cancellationReason || '—'} · {fmtDateTime(x.cancelledAt)}
        </Notice>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          <Section title={inbound ? 'Inbound lines' : 'Detail purchase'} flush>
            <TableData<PurchaseOrderLine>
              data={lines}
              columns={LINE_COLUMNS}
              size="small"
              className="rounded-none border-0 shadow-none"
            />
          </Section>
          <Section title="Goods receipt" flush>
            <TableData<GoodsReceiptSummary>
              data={goodsReceipts}
              columns={RECEIPT_COLUMNS}
              size="small"
              className="rounded-none border-0 shadow-none"
              emptyState={<EmptyState title="Belum ada goods receipt" />}
            />
          </Section>
        </div>

        <div className="flex flex-col gap-5">
          <Section title="Info">
            <KV label={src.label} value={src.value} />
            <KV
              label="Hub Name"
              value={hubName(x.hub, x.hubID)}
              sub={x.hub?.code ?? undefined}
            />
            <KV label="Est. arrival" value={fmtDateTime(x.estArrivalAt)} />
            {x.notes && <KV label="Catatan" value={x.notes} />}
          </Section>
          {x.status === 'CONFIRMED' && (
            <Section title="Penerimaan">
              <KV
                label="Status penerimaan"
                value={
                  <StatusBadge map={RECEIPT_STATUS} value={x.receiptStatus} />
                }
              />
              <KV
                label="Progress"
                value={`${fmtQty(x.totalReceived)} / ${fmtQty(x.totalQty)}`}
              />
            </Section>
          )}
          <Section title="Riwayat">
            <Timeline entries={historyOf(x, goodsReceipts)} />
          </Section>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderDetailPage;
